// A small history router: enough for a handful of screens, no dependency.
import { useEffect, useState } from 'preact/hooks';
import { h, type ComponentChildren } from 'preact';

const listeners = new Set<() => void>();
export function navigate(to: string, opts: { replace?: boolean } = {}) {
  if (opts.replace) history.replaceState(null, '', to);
  else history.pushState(null, '', to);
  listeners.forEach((l) => l());
  window.scrollTo(0, 0);
}
window.addEventListener('popstate', () => listeners.forEach((l) => l()));

export function useLocation() {
  const [loc, setLoc] = useState(() => ({ path: location.pathname, search: location.search, hash: location.hash }));
  useEffect(() => {
    const l = () => setLoc({ path: location.pathname, search: location.search, hash: location.hash });
    listeners.add(l);
    return () => void listeners.delete(l);
  }, []);
  return loc;
}

/** match('/r/:id', '/r/12') → { id: '12' } */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split('/').filter(Boolean);
  const a = path.split('/').filter(Boolean);
  if (p.length !== a.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(':')) params[p[i].slice(1)] = decodeURIComponent(a[i]);
    else if (p[i] !== a[i]) return null;
  }
  return params;
}

interface LinkProps {
  href: string;
  children?: ComponentChildren;
  class?: string;
  style?: string;
  target?: string;
  rel?: string;
  onClick?: (e: MouseEvent) => void;
  'aria-label'?: string;
  'aria-current'?: 'page' | undefined;
}

export function Link({ href, onClick, children, ...rest }: LinkProps) {
  const handle = (e: MouseEvent) => {
    onClick?.(e);
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    if (/^https?:/.test(href)) return;
    e.preventDefault();
    navigate(href);
  };
  // h() rather than JSX: Preact 11's strict anchor union types reject spread props.
  return h('a', { ...rest, href, onClick: handle } as Record<string, unknown>, children);
}
